import React from 'react';
import { Document, Font, Image, Page, pdf, StyleSheet, Text, View } from '@react-pdf/renderer';
import Icon from '@components/@core/Icon';
import Header from '@components/PdfHeader';
import isNil from 'ramda/src/isNil';
import length from 'ramda/src/length';
import range from 'ramda/src/range';
import { getUnit } from '~/services/util';

const styles = StyleSheet.create({
    container: {
        flexDirection: 'column',
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 20,
        alignItems: 'stretch',
    },
    mainHeader: {
        display: 'flex',
        flexDirection: 'column',
        margin: '0px',
        paddingBottom: '3px',
        paddingTop: '10px',
    },
    listHeader: {
        display: 'flex',
        flexDirection: 'row',
        marginLeft: '10px',
        marginRight: '10px',
        marginBottom: '10px',
        paddingBottom: '10px',
        paddingTop: '10px',
        borderBottomWidth: 0.25,
        borderBottomColor: 'black',
        borderBottomStyle: 'solid',
    },
    listItem: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: '10px',
        marginRight: '10px',
        marginLeft: '10px',
        borderBottomWidth: 0.25,
        borderBottomColor: 'black',
        borderBottomStyle: 'solid',
        minHeight: 55,
    },
    pageNumbers: {
        position: 'absolute',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        bottom: 10,
        left: 0,
        right: 0,
        textAlign: 'center',
        margin: '10px',
        fontSize: 10,
        fontFamily: 'Roboto',
        fontWeight: 'normal',
    },
    ratings: {
        height: '13px',
        width: '13px',
        color: 'black',
        border: '2px',
        borderStyle: 'solid',
    },
    scorecardTitle: {
        fontSize: 14,
        fontWeight: 'normal',
        fontFamily: 'Roboto',
    },
    fontStyle1: {
        fontSize: '9',
        fontWeight: 'light',
        fontFamily: 'Roboto',
    },
    fontStyle2: {
        fontSize: '12',
        fontWeight: 'bold',
        fontFamily: 'Roboto',
    },
});

Font.register({
    family: 'Roboto',
    fonts: [
        {
            src: 'https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf',
            fontWeight: 'normal',
        },
        {
            src: 'https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf',
            fontWeight: 'bold',
        },
        {
            src: 'https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf',
            fontWeight: 'light',
        },
    ],
});

const rating = (score) => {
    const ratingArray = range(0, 5);
    return (
        <View style={{ flex: 1, flexDirection: 'row', marginBottom: '2px', marginTop: '2px' }}>
            {ratingArray.map((i, index) => (
                <Image src={i >= score ? '/images/ratings_active.png' : '/images/star.png'} key={index} style={styles.ratings} />
            ))}
        </View>
    );
};

export const ScorecardDetailPdf = (data, quotas) => {
    let pageIndex = 1;
    pdf(
        <Document>
            <Page size="letter" style={styles.container} wrap>
                <Header style={{ paddingTop: 20 }} header="Quotas" fixed />
                <View wrap style={{ display: 'flex' }}>
                    <View style={styles.mainHeader} fixed>
                        <Text style={styles.scorecardTitle}>{data.title}</Text>
                    </View>
                    <View style={styles.listHeader} fixed>
                        <View style={{ flex: 1 }} />
                        <View style={{ flex: 9 }}>
                            <Text style={styles.fontStyle1}>{`Quotas (${length(data.data.quotas)})`}</Text>
                        </View>
                        <View style={{ flex: 3 }}>
                            <Text style={[styles.fontStyle1, { textAlign: 'right' }]}>Target</Text>
                        </View>
                        <View style={{ flex: 3 }}>
                            <Text style={[styles.fontStyle1, { textAlign: 'right' }]}>Actual</Text>
                        </View>
                        <View style={{ flex: 2 }} />
                        <View style={{ flex: 3, marginRight: 5 }}>
                            <Text style={styles.fontStyle1}>Rating Scale</Text>
                        </View>
                    </View>
                    {quotas.map((quota, index) => {
                        const stars = isNil(quota.stars) ? quota.star_values : quota.stars;
                        let pageBreak = false;
                        if (pageIndex === 8) {
                            pageBreak = true;
                            pageIndex = 1;
                        }
                        pageIndex++;
                        return (
                            <View style={styles.listItem} key={index} wrap break={pageBreak}>
                                <View style={{ flex: 1 }}>
                                    <Text style={styles.fontStyle2}>{index + 1}</Text>
                                </View>
                                <View style={{ flex: 9 }}>
                                    <Text style={styles.fontStyle2}>{quota.name}</Text>
                                    <Text style={styles.fontStyle1}>{quota.data.description}</Text>
                                </View>
                                <View style={{ flex: 3 }}>
                                    <Text style={[styles.fontStyle2, { textAlign: 'right' }]}>{getUnit(stars[5], quota.data.target_types)}</Text>
                                </View>
                                <View style={{ flex: 3 }}>
                                    <Text style={[styles.fontStyle2, { textAlign: 'right' }]}>{getUnit(stars[5], quota.data.target_types)}</Text>
                                    <Text style={[styles.fontStyle2, { textAlign: 'right' }]}>{getUnit(stars[4], quota.data.target_types)}</Text>
                                    <Text style={[styles.fontStyle2, { textAlign: 'right' }]}>{getUnit(stars[3], quota.data.target_types)}</Text>
                                    <Text style={[styles.fontStyle2, { textAlign: 'right' }]}>{getUnit(stars[2], quota.data.target_types)}</Text>
                                    <Text style={[styles.fontStyle2, { textAlign: 'right' }]}>{getUnit(stars[1], quota.data.target_types)}</Text>
                                    <Text style={[styles.fontStyle2, { textAlign: 'right' }]}>{getUnit(stars[0], quota.data.target_types)}</Text>
                                </View>
                                <View style={{ flex: 2 }} />
                                <View style={{ flex: 3, marginRight: 5 }}>
                                    {rating(5)}
                                    {rating(4)}
                                    {rating(3)}
                                    {rating(2)}
                                    {rating(1)}
                                    {rating(0)}
                                </View>
                            </View>
                        );
                    })}
                </View>
                <Text fixed style={styles.pageNumbers} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
            </Page>
        </Document>
    )
        .toBlob()
        .then((e) => {
            const url = URL.createObjectURL(e);
            window.open(url, '__blank');
        });
};

const PdfIcon = ({ state, props, name }) => {
    const { data } = props;
    const { quotas } = state;
    const handlePdf = () => {
        ScorecardDetailPdf(data, quotas);
    };

    if (name) {
        handlePdf();
        return null;
    }

    return <Icon name="fas fa-print" size={16} color="#343f4b" onClick={handlePdf} />;
};

export default PdfIcon;
